import React, { BaseSyntheticEvent, useState } from 'react';
import { useMutate } from 'restful-react';
import { TextInput } from '../../FormComponents/TextInput/TextInput';
import { BaseModal } from '../BaseModal/BaseModal';
import './StreamDestinationModal.scss';
import { Button } from '../../FormComponents/Buttons/Button';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import useProject from '../../../hooks/useProject';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { captionLanguages } from '../../../common/constants';
import { closedCaptionLanguages } from '../../../common/constants';
import { Dropdown } from '../../FormComponents/Dropdown/Dropdown';
import { ValidationError } from '../../FormComponents/ValidationError/ValidationError';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { Destination } from '../../../types/Destination';
import { Stream } from '../../../types/Stream';
import { CaptionModeEnum } from '../../../types/enums/CaptionModeEnum';
import { Option } from '../../../common/interfaces';
import useStripeCheckout from '../../../hooks/useStripeCheckout';
import env from '../../../Env';

type Props = {
  isOpen: boolean;
  contentLabel: string;
  selectedDestination: Destination;
  handleCloseModal: () => void;
};

type CreateDestinationFormData = {
  streamKey: string;
  streamUrl: string;
  name: string;
  languageIsoCode: string;
  streamId: string;
};

type DeleteDestinationFormData = {
  streamId: string;
  destinationId: string;
};

export const StreamDestinationModal = (props: Props) => {
  const { t } = useTranslation();
  const { project, setProject } = useProject();
  const { createSession, loadingSession } = useStripeCheckout();

  const defaultValues: CreateDestinationFormData = {
    streamKey: props.selectedDestination.streamKey,
    streamUrl: props.selectedDestination.streamUrl,
    streamId: project.stream.id,
    name: props.selectedDestination.name,
    languageIsoCode: props.selectedDestination.languageIsoCode,
  };

  const { control, errors, handleSubmit } = useForm<CreateDestinationFormData>({
    defaultValues,
  });

  // const removeDestination = (destinationId: string) => {
  //   if (project?.stream
  //     && project?.stream.encoders) {
  //       for(let i = 0; i < project?.stream?.encoders?.length; i++) {
  //         const encoder = project?.stream?.encoders[i];

  //         if (encoder.destinations) {
  //           const index = encoder.destinations.map((x) => x.id).indexOf(destinationId);

  //           if (index != null && index !== -1) {
  //             encoder.destinations.splice(index, 1);

  //             break;
  //           }
  //         }
  //       }
  //   }
  // };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulCreate = (body: any, data: any) => {
    if (data.statusCode === 200) {

      const newProject = {
        ...project,
        stream: data.result as Stream,
      };

      setProject(newProject);

      props.handleCloseModal();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulDelete = (body: any, data: any) => {
    if (data.statusCode === 200) {
      if (project) {        
        const newProject = {
          ...project,
          stream: data.result as Stream,
        };

        setProject(newProject);
                      
        const index = project.encoder.destinations?.map((x) => x.id).indexOf(body.destinationId);
        if (index != null && index !== -1) {
          project.encoder.destinations?.splice(index, 1);
        }
      }

      props.handleCloseModal();
    }
  };

  const [duplicateDestinationError, setDuplicateDestinationError] = useState(false);

  const {
    mutate: createDestination,
    loading: loadingCreate,
    error: errorCreate,
  } = useMutate<CreateDestinationFormData>({
    verb: 'PUT',
    path: '/destinations',
    onMutate: onSuccessfulCreate,
  });

  const {
    mutate: deleteDestination,
    loading: loadingDelete,
    error: errorDelete,
  } = useMutate<DeleteDestinationFormData>({
    verb: 'DELETE',
    path: '/destinations',
    onMutate: onSuccessfulDelete,
  });

  const onSubmit: SubmitHandler<CreateDestinationFormData> = (
    data: CreateDestinationFormData,
    event?: BaseSyntheticEvent,
  ) => {
    event?.preventDefault();
    data.languageIsoCode = data.languageIsoCode ?? 'en-us';
    data.streamId = project.stream.id;

    if(doesDestinationAlreadyExist(data.streamKey, data.streamUrl)){

      setDuplicateDestinationError(true);
  
      return false;
    }

    createDestination({ ...data, streamId: project.stream.id, encoderId: props.selectedDestination.encoderId });
  };

  const onDelete = (data: DeleteDestinationFormData) => {
    deleteDestination(data);
  };

  const isValidDestinationUrl = (ingestUrl: string) => {
    if (
      ingestUrl.toLowerCase().startsWith('rtmps://') ||
      ingestUrl.toLowerCase().startsWith('rtmp://') ||
      ingestUrl.toLowerCase().startsWith('srt://')
    ) {
      return true;
    }
    return false;
  };

  const doesDestinationAlreadyExist = (newStreamKey: string, newStreamUrl: string): boolean => {
    return project.encoder?.destinations?.some(
      dest => dest.streamKey + dest.streamUrl.toLowerCase() === newStreamKey + newStreamUrl.toLowerCase()
    ) ?? false;
  }

  const isOlderThan = (dateString: string, days: number): boolean => {
    const date = new Date(dateString);
    const now = new Date();
    const threshold = new Date();
    threshold.setDate(now.getDate() - days);
    return date < threshold;
  };

  const showFreeTrialText = (): Boolean => {
    if (project.createdByUser?.hasCard) {
      return false;
    }

    if (isOlderThan(project.createdByUser?.createdUtc ?? '', 7)) {
      return false;
    }

    return true;
  }

  const getLanguages = (): Option[] => {
    let options =
      project.encoder?.captionMode === CaptionModeEnum.Open
        ? captionLanguages
        : closedCaptionLanguages;

    if (project.createdByUser?.hasCard) {
      return options;
    }

    if ((project.stream.encoders?.length ?? 1) >= 2) {
      return project.stream.encoders?.flatMap(
        e => options.filter(x => x.value === e.languageIsoCode)
      ) ?? [];
    }

    return options;
  };

  if (errorCreate || errorDelete) {
    return <ErrorMessage message={t('error-messages.generic-error')} />;
  }

  return (
    <BaseModal
      isOpen={props.isOpen}
      contentLabel={props.contentLabel}
      handleCloseModal={props.handleCloseModal}
      ariaHideApp={true}
      title="producer.control-panel.stream-destinations.modal-title"
    >
      <form className="stream-destination-modal" onSubmit={handleSubmit(onSubmit)}>
        <ValidationError
          errorType={errors.streamUrl}
          errorMessageKey={errors.streamUrl?.message ?? ''}
        />
        <Controller
          name="streamUrl"
          control={control}
          rules={{
            required: true,
            maxLength: 250,
            validate: (value) => isValidDestinationUrl(value),
          }}
          render={({ onChange, value }) => (
            <>
              <label>{t('producer.control-panel.stream-destinations.stream-url-title')}</label>
              <TextInput
                placeholder="producer.control-panel.stream-destinations.stream-url-placeholder"
                value={value}
                onChange={onChange}
                onAfterChange={() => {
                  let url: URL;

                  try {
                    url = new URL(
                      control
                        .getValues()
                        .streamUrl.replace('rtmps', 'https')
                        .replace('rtmp', 'https')
                        .replace('srt', 'https'),
                    );
                  } catch {
                    return;
                  }
            
                  const overRides = ['YouTube', 'Vimeo', 'FACEBOOK', 'LINKEDIN', 'Wowza', 'Twitch', 'Dacast', 'IBM', 'UStream'];

                  let name = url.hostname.toLocaleLowerCase();

                  for (const index in overRides) {
                    if (name.indexOf(overRides[index].toLocaleLowerCase()) > -1) {
                      name = overRides[index];
                    }
                  }

                  if (name === 'UStream') {
                    name = 'IBM';
                  }

                  control.setValue('name', name, { shouldValidate: true, shouldDirty: true });
                }}
                errorType={errors.streamUrl}
                errorMessageKey={
                  value.length <= 250
                    ? 'producer.control-panel.stream-destinations.stream-url-missing-value'
                    : 'producer.control-panel.stream-destinations.stream-url-max-length'
                }
                readOnly={props.selectedDestination.id !== ''}
              />
            </>
          )}
        />
        <Controller
          name="streamKey"
          control={control}
          rules={{ required: true, maxLength: 500 }}
          render={({ onChange, value }) => (
            <>
              <label>{t('producer.control-panel.stream-destinations.stream-key-title')}</label>
              <TextInput
                placeholder="producer.control-panel.stream-destinations.stream-key-placeholder"
                value={value}
                onChange={onChange}
                onAfterChange={null}
                errorType={errors.streamKey}
                errorMessageKey={
                  value.length <= 500
                    ? 'producer.control-panel.stream-destinations.stream-key-missing-value'
                    : 'producer.control-panel.stream-destinations.stream-key-max-length'
                }
                readOnly={props.selectedDestination.id !== ''}
              />
            </>
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: true, maxLength: 150 }}
          render={({ onChange, value }) => (
            <>
              <label>{t('producer.control-panel.stream-destinations.stream-name-title')}</label>
              <TextInput
                placeholder="producer.control-panel.stream-destinations.stream-name-placeholder"
                value={value}
                onChange={onChange}
                onAfterChange={null}
                errorType={errors.name}
                errorMessageKey={
                  value.length <= 150
                    ? 'producer.control-panel.stream-destinations.stream-name-missing-value'
                    : 'producer.control-panel.stream-destinations.stream-name-max-length'
                }
                readOnly={props.selectedDestination.id !== ''}
              />
            </>
          )}
        />
        {project?.encoder?.captionMode !== CaptionModeEnum.None && (
          <Controller
            control={control}
            name="languageIsoCode"
            className="mb-3"
            rules={{ required: true }}
            render={({ onChange, value }) => (
              <>
                {props.selectedDestination.id !== '' && (
                  <p className="caption-description">
                    {t(`producer.control-panel.stream-destinations.caption-language`)}
                  </p>
                )}

                {props.selectedDestination.id === '' && (
                  <p className="caption-description">
                    {t(`producer.control-panel.stream-destinations.caption-language-title`)}
                  </p>
                )}

                <Dropdown
                  value={value || null}
                  options={getLanguages()}
                  onChange={(data) => onChange(data?.value)}
                  placeholderKey="producer.control-panel.stream-destinations.stream-language-placeholder"
                  errorType={errors.languageIsoCode}
                  errorMessageKey="producer.control-panel.stream-destinations.stream-language-missing-value"
                  readonly={
                    props.selectedDestination.id !== ''
                  }
                />
              </>
            )}
          />
        )}
        {(
         showFreeTrialText()
        ) && (
          <>
            <p className="free">
              Free accounts include two languages, including the source language. If you'd like to add further languages, please add your card details by clicking the "Update payment details" link below. Charges apply as soon as your stream ends.
            </p>
            <br />
            {!loadingSession ? (
              <button
                className="user-details__change"
                onClick={() => {
                  createSession({ stripePriceId: env.stripeSignUpPriceId });
                }}
              >
                {t('account.update-payment-details')}
              </button>
            ) : (
              <LoadingSpinner />
            )}
          </>
        )}
        {duplicateDestinationError && <div className="help is-danger">{t('producer.control-panel.stream-destinations.duplicate-destination')}</div>}
        <br />
        {props.selectedDestination.id === '' ? (
          <>
            {!loadingCreate ? (
              <Button
                translationKey="producer.control-panel.stream-destinations.set"
                variant="primary"
                color="blue"
                onClick={() => null}
                type="submit"
                disabled={loadingCreate}
              />
            ) : (
              <LoadingSpinner />
            )}
          </>
        ) : null}
      </form>
      {props.selectedDestination.id !== '' ? (
        <>
          {!loadingDelete ? (
            <Button
              translationKey="producer.control-panel.stream-destinations.delete"
              variant="secondary"
              color="blue"
              onClick={() => onDelete({ destinationId: props.selectedDestination.id, streamId: project.stream.id })}
              type={undefined}
              disabled={loadingDelete}
            />
          ) : (
            <LoadingSpinner />
          )}
        </>
      ) : null}
    </BaseModal>
  );
};
